import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { ArrowLeft, FileInput, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/lib/auth-context";
import { api } from "@/lib/api";

/**
 * Hand-entry form for a vendor invoice. The AI-extraction path lives in the
 * Upload Bill modal (see UploadBillModal). This page is for users who'd
 * rather type a bill in directly — e.g. a small reimbursement with no PDF.
 *
 * Vendor is optional: a bill can be saved with `vendor_id=null` and a free-
 * text `parsed_vendor_name`. The detail page lets the user link it to a real
 * vendor later via PATCH /api/ap-invoices/:id/vendor.
 */

interface Vendor {
  id: string;
  name: string;
}
interface VendorsResponse {
  vendors: Vendor[];
}

const STATUSES = [
  { value: "draft", label: "Draft" },
  { value: "open", label: "Open" },
  { value: "approved", label: "Approved" },
  { value: "on_hold", label: "On Hold" },
];

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

function addDays(d: string, days: number): string {
  const t = new Date(d);
  t.setUTCDate(t.getUTCDate() + days);
  return t.toISOString().slice(0, 10);
}

export default function NewApInvoicePage() {
  const { sessionId } = useAuth();
  const [, setLocation] = useLocation();
  const { toast } = useToast();

  // Header fields
  const [supplierInvoiceNumber, setSupplierInvoiceNumber] = useState("");
  const [vendorId, setVendorId] = useState<string>("");
  const [parsedVendorName, setParsedVendorName] = useState("");
  const [invoiceDate, setInvoiceDate] = useState(today());
  const [dueDate, setDueDate] = useState(addDays(today(), 30));
  const [paymentTerms, setPaymentTerms] = useState("NET30");
  const [currencyCode, setCurrencyCode] = useState("USD");
  const [subtotal, setSubtotal] = useState("0.00");
  const [taxAmount, setTaxAmount] = useState("0.00");
  const [freightAmount, setFreightAmount] = useState("0.00");
  const [totalAmount, setTotalAmount] = useState("0.00");
  const [status, setStatus] = useState("draft");
  const [notes, setNotes] = useState("");

  const { data: vendorsData } = useQuery<VendorsResponse>({
    queryKey: ["/api/vendors"],
    queryFn: () => api.get<VendorsResponse>(`/vendors`, sessionId),
    enabled: !!sessionId,
  });
  const vendors = vendorsData?.vendors || [];

  const createMutation = useMutation({
    mutationFn: async () => {
      return api.post<{ id: string; supplier_invoice_number?: string }>(`/ap-invoices`, {
        supplierInvoiceNumber: supplierInvoiceNumber.trim(),
        vendorId: vendorId || null,
        parsedVendorName: vendorId ? null : (parsedVendorName.trim() || null),
        invoiceDate,
        dueDate,
        paymentTerms,
        currencyCode,
        subtotal,
        taxAmount,
        freightAmount,
        totalAmount,
        notes: notes.trim() || null,
        status,
      }, sessionId);
    },
    onSuccess: (res) => {
      toast({ title: "Vendor invoice created" });
      setLocation(`/ap-invoices/${res.id}`);
    },
    onError: (e: Error) => toast({
      title: "Could not create vendor invoice",
      description: e.message,
      variant: "destructive",
    }),
  });

  const canSubmit =
    supplierInvoiceNumber.trim().length > 0 &&
    !!invoiceDate &&
    !!dueDate &&
    !createMutation.isPending;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <button
          onClick={() => setLocation("/ap-invoices")}
          className="text-muted-foreground hover:text-foreground transition-colors"
          data-testid="button-back"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-md bg-primary/10 flex items-center justify-center">
            <FileInput className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">New Vendor Invoice</h1>
            <p className="text-sm text-muted-foreground">
              Record a vendor bill manually. To upload a PDF instead, use the “Upload Bill” button on the AP Invoices list.
            </p>
          </div>
        </div>
      </div>

      <Card>
        <CardContent className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-sm font-medium">Supplier Invoice # *</label>
              <Input
                value={supplierInvoiceNumber}
                onChange={(e) => setSupplierInvoiceNumber(e.target.value)}
                placeholder="e.g. 12345-AB"
                data-testid="input-supplier-invoice-number"
              />
            </div>

            <div className="space-y-1">
              <label className="text-sm font-medium">Vendor</label>
              <Select value={vendorId || "_none"} onValueChange={(v) => setVendorId(v === "_none" ? "" : v)}>
                <SelectTrigger data-testid="select-vendor">
                  <SelectValue placeholder="Select vendor (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">— No vendor (resolve later) —</SelectItem>
                  {vendors.map((v) => (
                    <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!vendorId && (
                <Input
                  className="mt-2"
                  value={parsedVendorName}
                  onChange={(e) => setParsedVendorName(e.target.value)}
                  placeholder="Vendor name as printed on the bill (free text)"
                  data-testid="input-parsed-vendor-name"
                />
              )}
            </div>

            <div className="space-y-1">
              <label className="text-sm font-medium">Invoice Date *</label>
              <Input
                type="date"
                value={invoiceDate}
                onChange={(e) => setInvoiceDate(e.target.value)}
                data-testid="input-invoice-date"
              />
            </div>

            <div className="space-y-1">
              <label className="text-sm font-medium">Due Date *</label>
              <Input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                data-testid="input-due-date"
              />
            </div>

            <div className="space-y-1">
              <label className="text-sm font-medium">Payment Terms</label>
              <Input
                value={paymentTerms}
                onChange={(e) => setPaymentTerms(e.target.value)}
                placeholder="e.g. NET30"
                data-testid="input-payment-terms"
              />
            </div>

            <div className="space-y-1">
              <label className="text-sm font-medium">Currency</label>
              <Input
                value={currencyCode}
                onChange={(e) => setCurrencyCode(e.target.value.toUpperCase().slice(0, 8))}
                data-testid="input-currency-code"
              />
            </div>

            <div className="space-y-1">
              <label className="text-sm font-medium">Subtotal</label>
              <Input
                type="number"
                step="0.01"
                value={subtotal}
                onChange={(e) => setSubtotal(e.target.value)}
                data-testid="input-subtotal"
              />
            </div>

            <div className="space-y-1">
              <label className="text-sm font-medium">Tax Amount</label>
              <Input
                type="number"
                step="0.01"
                value={taxAmount}
                onChange={(e) => setTaxAmount(e.target.value)}
                data-testid="input-tax-amount"
              />
            </div>

            <div className="space-y-1">
              <label className="text-sm font-medium">Freight Amount</label>
              <Input
                type="number"
                step="0.01"
                value={freightAmount}
                onChange={(e) => setFreightAmount(e.target.value)}
                data-testid="input-freight-amount"
              />
            </div>

            <div className="space-y-1">
              <label className="text-sm font-medium">Total *</label>
              <Input
                type="number"
                step="0.01"
                value={totalAmount}
                onChange={(e) => setTotalAmount(e.target.value)}
                data-testid="input-total-amount"
              />
            </div>

            <div className="space-y-1">
              <label className="text-sm font-medium">Status</label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger data-testid="select-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUSES.map((s) => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-sm font-medium">Notes</label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Internal notes about this bill (optional)"
              className="min-h-[80px]"
              data-testid="textarea-notes"
            />
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => setLocation("/ap-invoices")}>
              Cancel
            </Button>
            <Button
              onClick={() => createMutation.mutate()}
              disabled={!canSubmit}
              data-testid="button-save"
            >
              {createMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save Vendor Invoice
            </Button>
          </div>
        </CardContent>
      </Card>

      <p className="text-xs text-muted-foreground">
        Add line items from the bill's detail page after saving.
      </p>
    </div>
  );
}
